import React, { useState } from "react";
import { Mail, Send, CheckCircle2, ArrowRight } from "lucide-react";
import api from "../api";
import SEO from "../components/SEO";

export default function Wholesale() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    phone: "",
    quantity_estimate: "",
    products_of_interest: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/wholesale/inquiry", form);
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to send inquiry");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-20">
      <SEO
        title="Wholesale Research Peptides — Bulk Pricing for Labs"
        description="Wholesale research peptide supplier for labs, clinics, and institutional researchers. 5-kit minimum (10 vials per kit). Custom volume pricing on BPC-157, Tirzepatide, Retatrutide, NAD+, and more. 24-hour quote turnaround."
        path="/wholesale"
        keywords="wholesale peptides, bulk peptide supplier, peptide wholesale USA, lab peptide pricing, institutional peptide accounts"
      />
      <div className="overline">BULK & WHOLESALE</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-light mt-3 max-w-3xl">
        Need volume? <span className="text-[#00C3FF]">Let's talk directly.</span>
      </h1>
      <p className="text-sm text-[#8A94A6] mt-4 max-w-2xl leading-relaxed">
        For bulk orders, institutional accounts, and research-program pricing, contact us with
        your requirements and we'll reply within 24 hours with a custom quote.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-12">
        <div className="bg-[#0C0C12] p-8 space-y-3">
          <Mail className="text-[#00C3FF]" size={20} />
          <div className="overline" style={{ fontSize: 10 }}>EMAIL</div>
          <div className="font-mono-tech text-sm text-white">support@prosourcelabs.org</div>
        </div>
        <div className="bg-[#0C0C12] p-8 space-y-3">
          <Send className="text-[#00C3FF]" size={20} />
          <div className="overline" style={{ fontSize: 10 }}>RESPONSE</div>
          <div className="font-mono-tech text-sm text-white">Within 24 hours</div>
        </div>
        <div className="bg-[#0C0C12] p-8 space-y-3">
          <ArrowRight className="text-[#00C3FF]" size={20} />
          <div className="overline" style={{ fontSize: 10 }}>MINIMUM</div>
          <div className="font-mono-tech text-sm text-white">5 unit minimum</div>
          <div className="text-xs text-[#8A94A6] leading-relaxed">
            Each unit = 10 vials at the dosage of your choice (mg / iu).
          </div>
        </div>
      </div>

      {sent ? (
        <div data-testid="wholesale-success" className="surface p-12 mt-12 text-center">
          <CheckCircle2 className="mx-auto text-[#00C3FF]" size={36} />
          <div className="overline mt-4">INQUIRY RECEIVED</div>
          <h2 className="font-heading text-3xl mt-2">Thank you.</h2>
          <p className="text-sm text-[#8A94A6] mt-3 max-w-md mx-auto">
            We've forwarded your request. Expect a reply at <span className="text-white">{form.email}</span> within 24 hours.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
          <div>
            <label className="overline block mb-2">Full Name *</label>
            <input data-testid="wholesale-name" required className="input-tech" value={form.name} onChange={update("name")} />
          </div>
          <div>
            <label className="overline block mb-2">Email *</label>
            <input data-testid="wholesale-email" type="email" required className="input-tech" value={form.email} onChange={update("email")} />
          </div>
          <div>
            <label className="overline block mb-2">Organization / Lab</label>
            <input data-testid="wholesale-org" className="input-tech" value={form.organization} onChange={update("organization")} />
          </div>
          <div>
            <label className="overline block mb-2">Phone</label>
            <input data-testid="wholesale-phone" className="input-tech" value={form.phone} onChange={update("phone")} />
          </div>
          <div>
            <label className="overline block mb-2">Estimated Volume</label>
            <input
              data-testid="wholesale-qty"
              className="input-tech"
              value={form.quantity_estimate}
              onChange={update("quantity_estimate")}
              placeholder="e.g. 50 vials/month"
            />
          </div>
          <div>
            <label className="overline block mb-2">Products of Interest</label>
            <input
              data-testid="wholesale-products"
              className="input-tech"
              value={form.products_of_interest}
              onChange={update("products_of_interest")}
              placeholder="e.g. Retatrutide, Ipamorelin"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="overline block mb-2">Message *</label>
            <textarea
              data-testid="wholesale-message"
              required
              minLength={10}
              rows={5}
              className="input-tech resize-none"
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us about your research and what you're looking for..."
            />
          </div>

          {err && (
            <div className="sm:col-span-2 text-xs font-mono-tech text-red-400 border-l-2 border-red-400 pl-3">
              {String(err)}
            </div>
          )}

          <div className="sm:col-span-2">
            <button data-testid="wholesale-submit" type="submit" disabled={busy} className="btn-primary">
              {busy ? "Sending…" : "Send Inquiry"} <Send size={14} />
            </button>
            <p className="text-[10px] font-mono-tech text-[#5C657A] mt-4 uppercase tracking-[0.2em]">
              We respond within 24 hours · No spam · For research purposes only
            </p>
          </div>
        </form>
      )}
    </div>
  );
}
